import { useState } from "react";
import { Product } from "@cabukaka/types";
import { Star, Store, MapPin, ShoppingCart, Check, ArrowRight } from "lucide-react";
import { NavLink } from "../../../components/ui/NavLink";
import { useCart } from "../../../hooks/cart/useCart";

interface ProductCardProps {
  product: Product;
  layout?: "grid" | "horizontal-scroll";
  onAddToCart?: (product: Product) => void;
  actionLabel?: string;
  showActionBtn?: boolean;
  showOriginalPrice?: boolean;
  showRating?: boolean;
  showReviewsCount?: boolean;
  showShopName?: boolean;
  showShopAddress?: boolean;
  showUnit?: boolean;
  showDescription?: boolean;
}

// SÉCURISATION DU PARSING DE PRIX POUR LES BADGES PROMO
const parsePrice = (priceStr: string | undefined): number => {
  if (!priceStr) return 0;
  // Nettoie la chaîne pour ne garder que les chiffres (ex: "15 000 FCFA" -> 15000)
  const cleaned = priceStr.replace(/[^0-9]/g, "");
  return parseInt(cleaned, 10) || 0;
};

// Composant Skeleton pour le layout grid
const GridSkeleton = () => (
  <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-sm border border-gray-100">
    <div className="relative h-48 md:h-56 w-full bg-gray-100 animate-pulse">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-50 to-gray-200"></div>
    </div>
    <div className="p-4 md:p-5 space-y-3">
      <div className="h-5 bg-gray-200 rounded-lg animate-pulse w-3/4"></div>
      <div className="space-y-1.5">
        <div className="h-3 bg-gray-200 rounded animate-pulse w-full"></div>
        <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
      </div>
      <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
      <div className="flex items-center justify-between gap-2 mt-4 pt-2">
        <div className="space-y-1">
          <div className="h-6 bg-gray-200 rounded animate-pulse w-20"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-14"></div>
        </div>
        <div className="w-24 h-9 bg-gray-200 rounded-xl animate-pulse"></div>
      </div>
    </div>
  </div>
);

// Composant Skeleton pour le layout horizontal-scroll
const HorizontalScrollSkeleton = () => (
  <div className="flex-shrink-0 w-[160px] md:w-[200px] bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-sm border border-gray-100">
    <div className="relative h-32 md:h-40 w-full bg-gray-100 animate-pulse">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-50 to-gray-200"></div>
    </div>
    <div className="p-3 md:p-4 space-y-2">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
      <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
      <div className="flex items-center justify-between gap-2 mt-3 pt-1">
        <div className="h-5 bg-gray-200 rounded animate-pulse w-14"></div>
        <div className="w-16 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
      </div>
    </div>
  </div>
);

export function ProductCard({
  product,
  layout = "grid",
  onAddToCart,
  actionLabel = "Ajouter",
  showActionBtn = true,
  showOriginalPrice = true,
  showRating = true,
  showReviewsCount = true,
  showShopName = true,
  showShopAddress = true,
  showUnit = true,
  showDescription = true,
}: ProductCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { isInCart } = useCart();
  
  const isVariantMode = product.priceMode === "variant";
  const inCart = !isVariantMode && isInCart(product.id);

  const defaultImage = "/images/product-placeholder.jpg";

  // RENDER DYNAMIQUE DU BOUTON (PANIER VS DÉTAILS)
  const renderButton = () => {
    if (!showActionBtn) return null;

    // CAS 1 : C'est un produit avec variantes -> Redirection vers la fiche produit
    if (isVariantMode) {
      return (
        <NavLink 
          to={`/products/${product.slug}`}
          className="px-3 md:px-4 py-2 text-xs md:text-sm rounded-xl font-bold bg-gray-100 text-gray-900 hover:bg-orange-600 hover:text-white transition-all duration-200 flex items-center gap-1 shadow-sm active:scale-95 group/btn"
        >
          <span>Options</span>
          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
        </NavLink>
      );
    }

    // CAS 2 : Produit standard déjà présent dans le panier
    if (inCart) {
      return (
        <button 
          className="px-3 md:px-4 py-2 text-xs md:text-sm rounded-xl font-bold bg-emerald-500 text-white cursor-not-allowed opacity-90 flex items-center gap-1 shadow-md shadow-emerald-500/10"
          disabled
        >
          <Check className="w-3.5 h-3.5 stroke-[3]" />
          <span>Ajouté</span>
        </button>
      );
    }

    // CAS 3 : Produit standard disponible à l'achat immédiat
    return (
      <button 
        onClick={(e) => {
          e.preventDefault(); // Évite de déclencher le lien du NavLink parent si imbriqué
          onAddToCart?.(product);
        }} 
        disabled={!product.inStock}
        className="px-3 md:px-4 py-2 text-xs md:text-sm rounded-xl font-bold bg-orange-600 text-white hover:bg-orange-700 hover:shadow-lg hover:shadow-orange-600/10 transition-all duration-200 flex items-center gap-1 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
      >
        <ShoppingCart className="w-3.5 h-3.5" />
        <span>{actionLabel}</span>
      </button>
    );
  };

  // CALCUL DU POURCENTAGE DE PROMO SÉCURISÉ
  const renderDiscountBadge = () => {
    if (!showOriginalPrice || !product.originalPrice) return null;
    const current = parsePrice(product.priceLabel);
    const original = parsePrice(product.originalPrice);
    
    if (original <= current) return null;
    const pct = Math.round(((original - current) / original) * 100);
    
    return (
      <div className="absolute top-2.5 right-2.5 bg-red-500 text-white text-[10px] md:text-xs font-black px-2 py-1 rounded-lg shadow-md z-10 uppercase tracking-wider animate-in fade-in duration-300">
        -{pct}%
      </div>
    );
  };

  // LAYOUT HORIZONTAL SCROLL
  if (layout === "horizontal-scroll") {
    return (
      <div className="flex-shrink-0 w-[160px] md:w-[200px] bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-sm hover:shadow-md border border-gray-100 transition-all duration-300 flex flex-col justify-between">
        <NavLink to={`/products/${product.slug}`} className="block relative aspect-[4/3] w-full bg-gray-50 overflow-hidden group">
          {!imageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
          )}
          <img
            src={imageError ? defaultImage : product.mainImage}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(true);
            }}
          />
          {renderDiscountBadge()}
        </NavLink>
        
        <div className="p-3 flex-1 flex flex-col justify-between">
          <div>
            <NavLink to={`/products/${product.slug}`}>
              <h3 className="text-xs md:text-sm font-bold text-gray-900 line-clamp-2 hover:text-orange-600 transition-colors leading-tight">
                {product.name}
              </h3>
            </NavLink>

            {showDescription && product.description && (
              <p className="text-[11px] text-gray-400 mt-1 line-clamp-1 leading-normal">{product.description}</p>
            )}

            {showShopName && product.shop && (
              <div className="flex items-center gap-1 mt-2 text-gray-400">
                <Store className="w-3 h-3 flex-shrink-0" />
                <span className="text-[11px] font-medium truncate">{product.shop.name}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-1 mt-3 pt-2 border-t border-gray-50">
            <div className="flex flex-col min-w-0">
              <span className="text-sm md:text-base font-black text-orange-600 truncate">
                {product.priceLabel}
              </span>
            </div>
            {renderButton()}
          </div>
        </div>
      </div>
    );
  }

  // LAYOUT GRID (DEFAULT)
  return (
    <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-0.5 border border-gray-100 transition-all duration-300 flex flex-col justify-between group">
      <NavLink to={`/products/${product.slug}`} className="block relative aspect-square w-full bg-gray-50 overflow-hidden">
        {!imageLoaded && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
        )}
        
        <img
          src={imageError ? defaultImage : product.mainImage}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(true);
          }}
        />
        
        {/* Badge de Rupture Moderne */}
        {!product.inStock && (
          <div className="absolute inset-0 bg-gray-950/40 backdrop-blur-[2px] flex items-center justify-center z-10">
            <span className="bg-gray-950 text-white px-3 py-1.5 rounded-xl text-xs font-bold tracking-wider uppercase shadow-lg border border-white/10">
              Rupture
            </span>
          </div>
        )}
        
        {renderDiscountBadge()}
      </NavLink>
      
      <div className="p-4 md:p-5 flex-1 flex flex-col justify-between">
        <div className="space-y-1">
          <NavLink to={`/products/${product.slug}`}>
            <h3 className="text-sm md:text-base font-bold text-gray-900 line-clamp-2 group-hover:text-orange-600 transition-colors leading-snug">
              {product.name}
            </h3>
          </NavLink>

          {showDescription && product.description && (
            <p className="text-xs text-gray-400 line-clamp-1 leading-normal">{product.description}</p>
          )}

          {/* Métadonnées Vendeur */}
          {(showShopName || showShopAddress) && product.shop && (
            <div className="pt-1.5 space-y-0.5">
              {showShopName && (
                <div className="flex items-center gap-1 text-gray-500">
                  <Store className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-xs font-semibold truncate hover:text-gray-700">{product.shop.name}</span>
                </div>
              )}
              {showShopAddress && product.shop.location?.city && (
                <div className="flex items-center gap-1 text-gray-400 pl-4">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="text-[11px] truncate">
                    {product.shop.location.city}
                    {product.shop.location.address && `, ${product.shop.location.address}`}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Avis et Évaluation */}
          {(showRating || showReviewsCount) && product.rating !== undefined && product.rating > 0 && (
            <div className="flex items-center gap-1.5 pt-1">
              <div className="flex items-center gap-0.5 bg-amber-50 px-1.5 py-0.5 rounded-md border border-amber-100">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                <span className="text-[11px] font-bold text-amber-900">{product.rating}</span>
              </div>
              {showReviewsCount && product.reviewsCount !== undefined && (
                <span className="text-[11px] text-gray-400 font-medium">({product.reviewsCount} avis)</span>
              )}
            </div>
          )}

          {showUnit && product.unit && (
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 pt-1">
              À {product.unit === 'piece' ? "l'unité" : "au poids"}
            </div>
          )}
        </div>

        {/* Zone de prix et CTAs */}
        <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-gray-50">
          <div className="flex flex-col min-w-0">
            <span className="text-base md:text-lg font-black text-orange-600 tracking-tight truncate">
              {product.priceLabel}
            </span>
            {showOriginalPrice && product.originalPrice && parsePrice(product.originalPrice) > parsePrice(product.priceLabel) && (
              <span className="text-[11px] text-gray-400 line-through font-medium truncate">
                {product.originalPrice} FCFA
              </span>
            )}
          </div>
          {renderButton()}
        </div>
      </div>
    </div>
  );
}

export function ProductCardGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <GridSkeleton key={index} />
      ))}
    </div>
  );
}

export function ProductCardHorizontalSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="flex gap-3 md:gap-4 overflow-x-auto pb-4 scrollbar-hide">
      {Array.from({ length: count }).map((_, index) => (
        <HorizontalScrollSkeleton key={index} />
      ))}
    </div>
  );
}